import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import apiService from '../../services/api'
import { Modal } from '../UI/Modal/Modal'
import { ContactResponse } from 'models/contacts'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'

const formShape = z.object({
  to: z.string({ message: 'Please enter a valid email address' }).email(),
  subject: z.string().min(1),
  from_id: z.number({ message: 'Please select a from email' }),
  body: z.string().min(1),
})

type formValues = z.infer<typeof formShape>

export default function SendEmailModal(props: {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  contact: ContactResponse
}) {
  const api = apiService()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      to: props.contact.email_preferred,
      subject: '',
      body: '',
    } as formValues,
    resolver: zodResolver(formShape),
    mode: 'all',
  })

  useMemo(() => {
    if (emailIntegrationOptions?.length === 1) {
      setValue('from_id', emailIntegrationOptions[0].value)
    }
  }, [emailIntegrationOptions])

  const toOptions = useMemo(() => {
    const options = []
    if (props.contact.email_preferred) {
      options.push({
        label: props.contact.email_preferred,
        value: props.contact.email_preferred,
      })
    }
    if (props.contact.email_work) {
      options.push({
        label: props.contact.email_work,
        value: props.contact.email_work,
      })
    }
    if (props.contact.email_personal) {
      options.push({
        label: props.contact.email_personal,
        value: props.contact.email_personal,
      })
    }
    if (props.contact.email_generic) {
      options.push({
        label: props.contact.email_generic,
        value: props.contact.email_generic,
      })
    }
    // Check unique values
    const uniqueOptions = options.filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    )
    return uniqueOptions
  }, [props.contact])

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true)

    await api.emailIntegration.sendEmailMessage(values.from_id, {
      to: [
        {
          name: `${props.contact.first_name} ${props.contact.last_name}`,
          email: values.to,
        },
      ],
      subject: values.subject,
      body: values.body,
    })
    setIsSubmitting(false)
    props.onSubmitSuccess?.()
    props.handleClose()
  })
  return (
    <Modal
      size="lg"
      open={props.show}
      onOpenChange={(open) => !open && props.handleClose()}
      title={'Send Email'}
      onAccept={onSubmit}
      acceptButtonText={isSubmitting ? 'Sending...' : 'Send'}
      loading={isSubmitting}
      cancelButtonText={isSubmitting ? 'Cancel' : 'Close'}
      onCancel={props.handleClose}
    >
      <CreatableSelect
        label={'To'}
        control={control}
        name="to"
        placeholder="To email"
        disableCreate
        options={toOptions}
        className={'flex-1 mt-0'}
        onCreateOption={() => Promise.resolve(null)}
        isClearable={false}
      />
      <CreatableSelect
        label={'From'}
        control={control}
        name="from_id"
        placeholder="From email"
        disableCreate
        options={emailIntegrationOptions}
        className={'flex-1 mt-0'}
        onCreateOption={() => Promise.resolve(null)}
        isClearable={false}
      />
      <TextInput
        control={control}
        name="subject"
        type="text"
        label="Subject"
        placeholder="Subject"
      />
      <StyledEditorRHF
        name={'body'}
        className={'h-28'}
        control={control}
        label={'Email Content'}
        placeholder={'Email Content'}
      />
    </Modal>
  )
}
